<template>
    <section>
        <div class="header-line grid-container">
            <h3 class="profile-label grid-item">
                Notes
            </h3>

            <div class="input-row">
                <ds-dropdown class="dropdown" position="bottom-end">
                    <ds-filled-button trailing-icon="chevron-down">
                        Add a note
                    </ds-filled-button>

                    <div slot="menu">
                        <div class="dropdown-menu-item" @click="showAddNotesModal">
                            <ds-icon class="add-icon" name="add-fill" />
                            <div class="add-a-note-label">
                                Add a note
                            </div>
                        </div>

                        <div v-if="contactPermissionDTO.canUseActivityHistoryTemplates">
                            <div class="note-from-template">
                                Add a note from template
                            </div>

                            <ul class="notes-dropdown-menu-list">
                                <li
                                    v-for="temp in templateObject"
                                    :key="temp.value"
                                    class="dropdown-menu-item"
                                    @click="saveFromTemplate(temp.value)"
                                >
                                    {{ temp.label }}
                                </li>
                            </ul>
                        </div>

                        <div
                            slot="footer"
                            class="footer"
                            @click="viewMangeNotesTemplate"
                        >
                            <a>Manage note templates</a>
                        </div>
                    </div>
                </ds-dropdown>
            </div>

            <notes-form
                ref="notesModal"
                :note-dropdowns="noteDropdowns"
                :contact="contact"
                :user-id="userId"
            />
        </div>
        <div v-if="!isContactHasNotes" class="no-notes-section">
            <p class="no-notes-label">
                No notes on {{ contact.firstName }}
            </p>
        </div>
        <div class="table-scroller">
            <div v-if="isContactHasNotes">
                <table class="notes-details">
                    <tr
                        v-for="(note, i) in notes"
                        :key="i"
                        class="note-details"
                        @click="showEditNotesModal(note)"
                    >
                        <td style="width: 5%">
                            <ds-icon name="notes" />
                        </td>

                        <td style="width: 75%">
                            <div class="notes-title">
                                {{ note.title }}
                            </div>

                            <p class="notes-body">
                                {{ note.contents }}
                            </p>

                            <p class="notes-assigned">
                                Assigned to: {{ getAssignName(note.assignedTo) }}
                            </p>
                        </td>

                        <td style="width: 20%">
                            <div class="created-date">
                                {{ dateFormatDate(note.lastUpdated, 'MM/DD/YYYY') }}
                            </div>
                        </td>
                    </tr>
                </table>
            </div>
        </div>
        <div v-if="isNotesLimitOver" class="view-all-notes-div" @click="showAllNotesModal">
            <span>Showing {{ getNotesLength() }} of {{ notesCount }}
                <ds-text-button class="show-all-notes-button">
                    View all notes
                </ds-text-button>
            </span>
        </div>
        <contact-notes-modal
            ref="notesModalPopUp"
            :contact="contact"
            :notes-count="notesCount"
            :user-id="userId"
            :note-dropdowns="noteDropdowns"
        />
    </section>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import NotesForm from './NotesForm';
import ContactNotesModal from './ContactNotesModal';

export default {
    components: {
        NotesForm,
        ContactNotesModal,
    },

    props: {
        contact: Object,
        userId: [String, Number],
    },

    data() {
        return {
            notes: [],
            notesCount: 0,
            MAX_NOTES_LIMIT: 10,
            loaded: false,
            options: {},
            templateObject: [],
            templateId: 0,
            noteDropdowns: {},
            contactPermissionDTO: {},
        };
    },

    created() {
        if (!this.loaded) {
            this.initTab();
            this.loaded = true;
        }
        this.$root.$on('CONTACTPERMISSIONS', (contactPermissions) => {
            this.contactPermissionDTO = contactPermissions;
        });
    },

    mounted() {
        this.$root.$on('DROPDOWN_LOADED', (data) => {
            this.options = data;
        });

        this.$root.$on('NOTES_STATUS', (data) => {
            if (data) {
                this.initTab();
            }
        });
    },

    beforeDestroy() {
        this.$root.$off('NOTES_STATUS');
    },

    computed: {
        isContactHasNotes() {
            return !!(this.notes && this.notes.length > 0);
        },

        isNotesLimitOver() {
            return this.notesCount > this.MAX_NOTES_LIMIT;
        },
    },

    methods: {

        initTab() {
            this.loadContactNotes();
            this.getNotesDropDowns();
        },

        getNotesLength() {
            return this.notes && this.notes.length > 0 ? this.notes.length : 0;
        },

        viewMangeNotesTemplate() {
            window.open('/SystemTemplate/manageTemplate.jsp?view=add&master=true&TemplateKind=Note');
        },

        showAllNotesModal() {
            this.$refs.notesModalPopUp.showAllNotesModal();
        },

        async saveFromTemplate(tempId) {
            try {
                await axios.post(`/app/note/saveFromTemplateField/${tempId}/${this.contact.id}`);

                const successMessage = 'Note added from template';

                this.$toast({ message: successMessage });
                this.$root.$emit('NOTES_STATUS', true);
            } catch (error) {
                this.$error({ message: 'error occurred while saving the note form template ' });
            }
        },

        async getNotesDropDowns() {
            try {
                const response = await axios.get('/app/note/loadDropDown');

                this.noteDropdowns = response.data;
                const templateDrop = response.data.templateList;

                this.templateObject = Object.keys(templateDrop).map((key) => {
                    return { label: templateDrop[key], value: Number(key) };
                }).sort((prev, next) => prev.label.localeCompare(next.label));
            } catch (error) {
                this.$error({ message: 'error occurred while getting drop downs' });
            }
        },

        getAssignName(assignId) {
            const owner = this.options.owners && this.options.owners.find((user) => user.value === assignId);

            return owner && Object.values(owner)[0] ? Object.values(owner)[0] : '';
        },

        async loadContactNotes() {
            try {
                const response = await axios.get(`/app/note/loadAllNotes/${this.contact.id}/${this.MAX_NOTES_LIMIT}`);

                this.notes = response.data.notesDetailsList;

                if (this.notes === null) {
                    this.notes = [];
                }
                this.notesCount = response.data.notesCount;
            } catch (error) {
                this.$error({ message: 'error occurred while loading notes ' });
            }
        },

        dateFormatDate(date, format = 'll') {
            const formattedDate = moment(date).isValid() ? date : new Date(date);

            return moment(formattedDate).format(format);
        },

        showAddNotesModal() {
            this.$refs.notesModal.showModal(null, false);
        },

        showEditNotesModal(note) {
            this.$refs.notesModal.showModal(note, false);
        },
    },
};

</script>

<style lang="scss" scoped>
.notes-details {
    cursor: pointer;
    border-collapse: collapse;
    border-top: 1px solid $color-gray-lighter;
    width: 100%;
    margin-top: $spacing-250;
}

.header-line {
    width: 100%;
}

.created-date {
    font-size: $font-size-xs;
    text-align: right;
    color: $color-gray-darker;
}

.grid-container {
    display: flex;
    margin: 0;
    margin-top: $spacing-250;
}

.grid-item {
    margin: auto;
}

.profile-label {
    margin-left: 0;
}

.notes-details > tr:last-child {
    border-bottom: 0;
}

.no-notes-section {
    width: 100%;
    text-align: center;
    border-top: 1px solid $color-gray-lighter;
    margin-top:  $spacing-200;
}

.no-notes-label {
    color: $color-gray-darker;
    font-size: $font-size-lg;
    font-weight: 300;
    padding-top: $spacing-800;
    padding-bottom: $spacing-600;
}

.notes-title {
    font-size: $font-size-md;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.notes-assigned {
    font-size: $font-size-xs;
    margin: 0;
    line-height: 1.5;
}

.notes-body {
    font-size: $font-size-xs;
    margin: 0;
    color: $color-gray-darker;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

@media only screen and ($medium-down) {
    .notes-body {
        white-space: nowrap;
        width: 12.5rem;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .notes-title {
        white-space: nowrap;
        width:  12.5rem;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}
@media only screen and (max-width: px-to-rem(610px)) {
    .created-date {
        width: 4.5rem;
    }
}

.input-row {
    width : auto;
    --dropdown-height: #{px-to-rem(330px)};
}

.view-all-notes-div {
    text-align: center;
    margin-top: 1.75rem;
}

.notes-dropdown-menu-list {
    padding: 0.01rem 0;
}

.add-icon {
    color: $color-blue;
}

.add-a-note-label {
    margin-left: $spacing-100;
}

.note-from-template {
    color: $color-gray;
    margin-left: px-to-rem(10px);
}

.footer {
    position: sticky;
    bottom: 0;
    background-color: $color-white;
    height: 3.5rem;
}

.table-scroller{
    flex-flow: row nowrap;
    overflow-x: auto;
}

.table-scroller::-webkit-scrollbar {
    display: none;
}

.note-details:hover {
    background-color: $color-gray-20;
}
</style>
